/**
 * @file Email_Component.tsx
 * @description Premium, responsive Email Input for Apex API.
 * Features ultra-premium styling and Web-safe interaction logic.
 */

import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, Animated, Platform } from 'react-native';
import { emailValidation } from '../validations/emailValidation';
import { useTheme } from '../context/themeContext';

interface EmailComponentProps {
  onValueChange?: (email: string, isValid: boolean) => void;
  onBlur?: (e?: any) => void;
  defaultValue?: string;
  isReadOnly?: boolean;
  value?: string;
  error?: string;
  touched?: boolean;
  required?: boolean;
  placeholder?: string;
  onSubmitEditing?: () => void;
  returnKeyType?: "done" | "go" | "next" | "send" | "search";
}

const Email_Component: React.FC<EmailComponentProps> = ({
  onValueChange,
  onBlur,
  defaultValue = '',
  isReadOnly = false,
  value,
  error: externalError,
  touched,
  required = false,
  placeholder = 'e.g. gokul@gmail.com',
  onSubmitEditing,
  returnKeyType
}) => {
  const { theme } = useTheme();
  const { colors } = theme;

  const [internalEmail, setInternalEmail] = useState<string>(defaultValue);
  const [internalError, setInternalError] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [wasBlurred, setWasBlurred] = useState<boolean>(false);
  const [shakeAnimation] = useState(new Animated.Value(0));

  const email = value ?? internalEmail;
  const error = externalError ?? internalError;
  // Show the validation error live while the user is typing (once they've
  // entered something), and after blur. It disappears automatically once the
  // email is valid. Empty field stays clean.
  const isErrorVisible =
    touched !== undefined
      ? (touched && !!error)
      : ((wasBlurred || email.length > 0) && !!error);

  /* ================= WEB AUTOFILL FIX (NO UI CHANGE) ================= */
useEffect(() => {
  if (Platform.OS === 'web' && typeof globalThis !== 'undefined') {
    const doc = (globalThis as any).document;
    if (!doc) return;

    const style = doc.createElement('style');
    style.textContent = `
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus,
      input:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 1000px ${colors.card} inset !important;
        box-shadow: 0 0 0 1000px ${colors.card} inset !important;
        -webkit-text-fill-color: ${colors.ink} !important;
        caret-color: ${colors.ink} !important;
        transition: background-color 9999s ease-in-out 0s !important;
      }
    `;
    doc.head.appendChild(style);

    return () => {
      if (style.parentNode) style.parentNode.removeChild(style);
    };
  }
}, [colors.card, colors.ink]);

  /* ================================================================ */

  useEffect(() => {
    setInternalEmail(defaultValue);
  }, [defaultValue]);

  /**
   * `propagate` decides whether the result is pushed back to the parent.
   *
   * It must be FALSE for the validate-on-value-change effect below. That effect
   * runs on mount with whatever `email` is at first render — '' for a controlled
   * parent that hydrates in its own useEffect, because the child renders before
   * the parent's effect runs. Propagating from there fired
   * `onValueChange('', false)` AFTER the parent had set the real address, wiping
   * a pre-filled email back to empty and showing "Email is required" on a record
   * that has one. Propagation belongs to real user input only.
   */
  const validateEmail = async (value: string, propagate = true) => {
    try {
      await emailValidation.validate(value);
      setInternalError('');
      if (propagate && onValueChange) onValueChange(value, true);
    } catch (err: any) {
      setInternalError(err.message);
      if (propagate && onValueChange) onValueChange(value, false);
    }
  };

  const handleBlur = (e: any) => {
    setIsFocused(false);
    setWasBlurred(true);
    void validateEmail(email, false);

    if (error || email.length === 0) {
      triggerShake();
    }
    if (onBlur) onBlur(e); 
  };

  const triggerShake = () => {
    Animated.sequence([
      Animated.timing(shakeAnimation, { toValue: 8, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: -8, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: 0, duration: 50, useNativeDriver: true })
    ]).start();
  };

  // Keep the inline error in step with the value, WITHOUT reporting upward — see
  // the note on validateEmail.
  useEffect(() => {
    void validateEmail(email, false);
  }, [email]);

  /**
   * Border/focus states are deliberately IDENTICAL to CommonInput's, because this
   * field sits beside CommonInput fields (Name next to Email on the candidate
   * card) and any difference reads as one of the two being broken. Previously
   * this box was taller (44 vs 38), bordered in `lineStrong` rather than `line`,
   * grew its border 1.5 → 2px on focus instead of showing a glow ring, and
   * flooded its background on error — four visible mismatches in one row.
   */
  const getWrapperStyle = () => {
    if (isReadOnly) {
      return [styles.inputReadOnly, { borderColor: colors.line, backgroundColor: colors.paper2 }];
    }
    if (isFocused) {
      return [
        { borderColor: colors.accent, backgroundColor: colors.card },
        Platform.OS === 'web' ? ({ boxShadow: `0 0 0 3px ${colors.accentSoft}` } as any) : null,
      ];
    }
    if (isErrorVisible) {
      return [
        { borderColor: colors.danger, backgroundColor: colors.card },
        Platform.OS === 'web' ? ({ boxShadow: `0 0 0 3px ${colors.dangerSoft}` } as any) : null,
      ];
    }
    return [styles.inputDefault, { borderColor: colors.line }];
  };

  let textColor = colors.ink;
  if (isReadOnly) {
    textColor = colors.ink3;
  } else if (error && email.length > 0) {
    textColor = colors.danger;
  }

  return (
    <View style={styles.container}>
      <View style={styles.labelRow}>
        <Text style={[styles.label, { color: colors.ink3 }]}>
          Email Address
          {required && <Text style={{ color: colors.danger, fontSize: 15 }}> *</Text>}
        </Text>
        {isReadOnly && (
          <View style={[styles.readOnlyBadge, { backgroundColor: colors.paper2, borderColor: colors.line }]}>
            <Text style={[styles.readOnlyText, { color: colors.ink3 }]}>SECURED</Text>
          </View>
        )}
      </View>
      
      <Animated.View
        style={[
          styles.inputWrapper,
          { backgroundColor: colors.card },
          { transform: [{ translateX: shakeAnimation }] },
          getWrapperStyle(),
        ]}
      >
        <TextInput
          value={email}
          onChangeText={(text) => {
            if (value === undefined) setInternalEmail(text);
            if (onValueChange) onValueChange(text, true); // We'll let Formik handle valid state if value is provided
            if (internalError) validateEmail(text);
          }}
          onFocus={() => {
            if (!isReadOnly) setIsFocused(true);
          }}
          onBlur={handleBlur}
          editable={!isReadOnly}
          placeholder={isReadOnly ? "" : placeholder}
          placeholderTextColor={colors.ink4}
          style={[
            styles.input,
            {
              color: textColor,
              ...Platform.select({
                android: {
                  fontFamily: '', // Forces system default
                  fontWeight: undefined, // Fixes emoji fallback
                },
                web: {
                  outlineStyle: 'none',
                } as any,
              }),
            },
          ]}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="off"
          underlineColorAndroid="transparent"
          {...(Platform.OS === 'web' ? { autoComplete: 'new-password' } as any : {})}
          onSubmitEditing={onSubmitEditing}
          returnKeyType={returnKeyType}
        />
      </Animated.View>

      {isErrorVisible && <Text style={[styles.errorLabel, { color: colors.danger }]}>{error}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { width: '100%' },
  // Label metrics copied from CommonInput — see getWrapperStyle.
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
    paddingHorizontal: 2,
  },
  label: {
    fontSize: 10.5,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 1.2,
  },
  readOnlyBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 2,
    borderWidth: 1,
  },
  readOnlyText: {
    fontSize: 9,
    fontWeight: '900',
    letterSpacing: 1,
  },
  inputWrapper: {
    minHeight: 38,
    borderRadius: 2,
    borderWidth: 1.5,
    paddingHorizontal: 12,
    paddingVertical: 4,
    justifyContent: 'center',
    overflow: 'hidden',
    ...Platform.select({
      web: {
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        boxShadow: '0 1px 2px 0 rgba(16, 24, 40, 0.04)',
      } as any,
      default: {},
    }),
  },
  inputDefault: {},
  inputReadOnly: {
    opacity: 0.8,
  },
  input: {
    fontSize: 13,
    fontWeight: '600',
    height: '100%',
    paddingVertical: 0,
    width: '100%',
    backgroundColor: 'transparent',
  },
  errorLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: 6,
    marginLeft: 4,
  },
});

export default Email_Component;
